'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { buildLogoutUrl } from '@sso/auth-client';

import { SSO_AUTH_CONFIG } from '@workspace/lib/auth/config';

import { useAuth } from './auth-provider';
import { clientRefreshToken } from './client';

/**
 * How many milliseconds before token expiry to show the warning dialog.
 * 2 minutes gives the user enough time to react without being intrusive.
 */
const WARNING_BEFORE_EXPIRY_MS = 2 * 60 * 1000;

/**
 * Maximum safe delay for setTimeout (signed 32-bit integer).
 * Delays exceeding this value fire immediately in most JS engines.
 */
const MAX_TIMEOUT_MS = 2_147_483_647;

interface SessionTimeoutState {
  showWarning: boolean;
  secondsRemaining: number;
}

/**
 * Monitors the SSO session JWT expiry and manages a countdown warning.
 *
 * Shows a warning dialog `WARNING_BEFORE_EXPIRY_MS` before the token expires.
 * Provides an `extendSession` callback that silently refreshes the token via
 * the SSO portal's POST /api/auth/refresh endpoint, then triggers a soft
 * server re-render so the layout re-reads the updated cookie.
 *
 * When the countdown reaches zero or a refresh attempt fails, the user is
 * redirected to the SSO logout endpoint automatically.
 */
export function useSessionTimeout() {
  const { user } = useAuth();
  const router = useRouter();
  const [state, setState] = useState<SessionTimeoutState>({
    showWarning: false,
    secondsRemaining: 0,
  });

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isRefreshingRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const performLogout = useCallback(() => {
    clearTimers();
    setState({ showWarning: false, secondsRemaining: 0 });
    const returnUrl =
      typeof window !== 'undefined' ? window.location.origin : undefined;
    window.location.href = buildLogoutUrl(SSO_AUTH_CONFIG, returnUrl);
  }, [clearTimers]);

  // Use a ref so startCountdown can call performLogout without it being a dep
  const performLogoutRef = useRef(performLogout);
  performLogoutRef.current = performLogout;

  const startCountdown = useCallback((expiryMs: number) => {
    // Clear any existing countdown before starting a new one
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    countdownRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.floor((expiryMs - Date.now()) / 1000));
      setState((prev) => ({ ...prev, secondsRemaining: remaining }));

      if (remaining <= 0) {
        // Session has expired — force logout rather than leaving the user
        // on a page with an invalid session.
        performLogoutRef.current();
      }
    }, 1000);
  }, []);

  const scheduleWarning = useCallback(() => {
    clearTimers();
    setState({ showWarning: false, secondsRemaining: 0 });

    if (!user?.exp) return;

    const expiryMs = user.exp * 1000;
    const now = Date.now();
    const timeUntilWarning = expiryMs - now - WARNING_BEFORE_EXPIRY_MS;

    if (timeUntilWarning <= 0) {
      // Already inside the warning window
      const remaining = Math.max(0, Math.floor((expiryMs - now) / 1000));
      setState({ showWarning: true, secondsRemaining: remaining });
      startCountdown(expiryMs);
      return;
    }

    // Guard against setTimeout 32-bit integer overflow (>~24.8 days).
    // In practice this won't happen with a 15-minute session, but protects
    // against future changes or misconfigured exp values.
    if (timeUntilWarning > MAX_TIMEOUT_MS) return;

    warningTimerRef.current = setTimeout(() => {
      const remaining = Math.max(0, Math.floor((expiryMs - Date.now()) / 1000));
      setState({ showWarning: true, secondsRemaining: remaining });
      startCountdown(expiryMs);
    }, timeUntilWarning);
  }, [user?.exp, clearTimers, startCountdown]);

  const extendSession = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    try {
      await clientRefreshToken({ authConfig: SSO_AUTH_CONFIG });
      setState({ showWarning: false, secondsRemaining: 0 });
      clearTimers();
      // Trigger a soft server re-render so the layout re-reads the refreshed
      // cookie and passes the updated user (with new exp) through AuthProvider.
      router.refresh();
    } catch {
      // Refresh failed — force logout rather than leaving the user on an
      // expired page with no session-expiry UI.
      performLogout();
    } finally {
      isRefreshingRef.current = false;
    }
  }, [router, clearTimers, performLogout]);

  // Schedule (or reschedule) the warning whenever user.exp changes —
  // this covers initial mount and post-refresh re-renders.
  useEffect(() => {
    scheduleWarning();
    return clearTimers;
  }, [scheduleWarning, clearTimers]);

  return {
    showWarning: state.showWarning,
    secondsRemaining: state.secondsRemaining,
    extendSession,
    logout: performLogout,
  };
}
